// Components/OrderDetailModal.jsx
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog } from "@mui/material";
import { styled } from "@mui/material/styles";
import { getOrder } from "../../../services/order_services";
import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  RefreshCw,
  User,
  CreditCard,
  DollarSign,
  Percent,
  Coins
} from "lucide-react";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiBackdrop-root": {
    backdropFilter: "blur(6px)",
    backgroundColor: "rgba(0, 0, 0, 0.2)",
  },
  "& .MuiPaper-root": {
    width: "700px",
    maxWidth: "90vw",
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
  },
}));

const statusIcons = {
  pending: Clock,
  inprocess: RefreshCw,
  success: CheckCircle,
  failed: XCircle,
  default: AlertCircle
};

const statusColors = {
  pending: "text-yellow-600",
  inprocess: "text-blue-600",
  success: "text-green-600",
  failed: "text-red-600",
  default: "text-gray-600"
};

export default function OrderDetailModal({ order, open, onClose }) {
  const [selectedTab, setSelectedTab] = useState("details");

  const { data: orderDetails, isLoading } = useQuery({
    queryKey: ["order", order?.id],
    queryFn: () => getOrder(order?.id, "events,payments,logs"),
    enabled: open && !!order?.id
  });

  const StatusIcon = statusIcons[orderDetails?.data?.status] || statusIcons.default;
  const statusColor = statusColors[orderDetails?.data?.status] || statusColors.default;


  const agentInfo = orderDetails?.data?.meta?.agent_info || {};
  const commission = agentInfo.commission || 0;
  const serviceFee = agentInfo.service_fee || 0;
  const netAmount = (orderDetails?.data?.amount || 0) - serviceFee;
  const agentShare = netAmount * (commission / 100);

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <div className="p-6">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <RefreshCw className="animate-spin h-8 w-8 text-blue-600" />
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Order Details</h2>
              <div className="flex items-center">
                <StatusIcon className={`h-5 w-5 mr-2 ${statusColor}`} />
                <span className={`font-medium capitalize ${statusColor}`}>
                  {orderDetails?.data?.status}
                </span>
              </div>
            </div>

            <div className="border-b border-gray-200 mb-4">
              <nav className="flex space-x-4">
                <button
                  onClick={() => setSelectedTab("details")}
                  className={`py-2 px-3 text-sm font-medium ${
                    selectedTab === "details"
                      ? "border-b-2 border-blue-500 text-blue-600"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Details
                </button>
                <button
                  onClick={() => setSelectedTab("agent")}
                  className={`py-2 px-3 text-sm font-medium ${
                    selectedTab === "agent"
                      ? "border-b-2 border-blue-500 text-blue-600"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Agent Info
                </button>
                <button
                  onClick={() => setSelectedTab("timeline")}
                  className={`py-2 px-3 text-sm font-medium ${
                    selectedTab === "timeline"
                      ? "border-b-2 border-blue-500 text-blue-600"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Timeline
                </button>
                <button
                  onClick={() => setSelectedTab("payments")}
                  className={`py-2 px-3 text-sm font-medium ${
                    selectedTab === "payments"
                      ? "border-b-2 border-blue-500 text-blue-600"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Payments
                </button>
              </nav>
            </div>

            {selectedTab === "details" && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Transaction ID</p>
                    <p className="font-medium">{orderDetails?.data?.txnNumber}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Source</p>
                    <p className="font-medium capitalize">
                      {orderDetails?.data?.source?.replace("_", " ")}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Receiver</p>
                    <p className="font-medium">{orderDetails?.data?.receiver}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Operator</p>
                    <p className="font-medium">{orderDetails?.data?.operator}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Amount</p>
                    <p className="font-medium">
                      {orderDetails?.data?.amount} {orderDetails?.data?.currency}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Created At</p>
                    <p className="font-medium">
                      {new Date(orderDetails?.data?.createdAt).toLocaleString()}
                    </p>
                  </div>
                </div>

                {orderDetails?.data?.meta && (
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Additional Information</p>
                    <pre className="bg-gray-100 p-3 rounded text-sm overflow-auto max-h-40">
                      {JSON.stringify(orderDetails.data.meta, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}

            {selectedTab === "agent" && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 flex items-center p-3 bg-blue-50 rounded-lg">
                    <User className="h-5 w-5 text-blue-600 mr-2" />
                    <div>
                      <p className="text-sm text-gray-500">Agent</p>
                      <p className="font-medium">{agentInfo.name || "N/A"}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center p-3 bg-green-50 rounded-lg">
                    <DollarSign className="h-5 w-5 text-green-600 mr-2" />
                    <div>
                      <p className="text-sm text-gray-500">Order Amount</p>
                      <p className="font-medium">
                        {orderDetails?.data?.amount} {orderDetails?.data?.currency}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center p-3 bg-yellow-50 rounded-lg">
                    <CreditCard className="h-5 w-5 text-yellow-600 mr-2" />
                    <div>
                      <p className="text-sm text-gray-500">Service Fee</p>
                      <p className="font-medium">
                        {serviceFee} {orderDetails?.data?.currency}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center p-3 bg-purple-50 rounded-lg">
                    <Coins className="h-5 w-5 text-purple-600 mr-2" />
                    <div>
                      <p className="text-sm text-gray-500">Net Amount</p>
                      <p className="font-medium">
                        {netAmount} {orderDetails?.data?.currency}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center p-3 bg-blue-50 rounded-lg">
                    <Percent className="h-5 w-5 text-blue-600 mr-2" />
                    <div>
                      <p className="text-sm text-gray-500">Commission Rate</p>
                      <p className="font-medium">{commission}%</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center p-3 bg-green-50 rounded-lg">
                    <DollarSign className="h-5 w-5 text-green-600 mr-2" />
                    <div>
                      <p className="text-sm text-gray-500">Agent Share</p>
                      <p className="font-medium">
                        {agentShare.toFixed(2)} {orderDetails?.data?.currency}
                      </p>
                    </div>
                  </div>
                </div>
                
                {agentInfo.notes && (
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Agent Notes</p>
                    <p className="bg-gray-100 p-3 rounded text-sm">{agentInfo.notes}</p>
                  </div>
                )}
              </div>
            )}

            {selectedTab === "timeline" && (
              <div className="space-y-4">
                {orderDetails?.data?.events?.length > 0 ? (
                  <div className="space-y-3">
                    {orderDetails.data.events.map((event, index) => (
                      <div key={index} className="flex">
                        <div className="flex flex-col items-center mr-4">
                          <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                          {index < orderDetails.data.events.length - 1 && (
                            <div className="w-0.5 h-10 bg-gray-300 my-1"></div>
                          )}
                        </div>
                        <div className="flex-1 pb-4">
                          <p className="font-medium">{event.type}</p>
                          <p className="text-sm text-gray-500">
                            {new Date(event.createdAt).toLocaleString()}
                          </p>
                          {event.data && (
                            <pre className="text-xs mt-1 bg-gray-100 p-2 rounded overflow-auto max-h-20">
                              {JSON.stringify(event.data, null, 2)}
                            </pre>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No timeline events found</p>
                )}
              </div>
            )}

            {selectedTab === "payments" && (
              <div className="space-y-4">
                {orderDetails?.data?.paymentIntents?.length > 0 ? (
                  orderDetails.data.paymentIntents.map((payment, index) => (
                    <div key={index} className="border rounded-lg p-3">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <p className="text-sm text-gray-500">Status</p>
                          <p className="font-medium">{payment.status}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Amount</p>
                          <p className="font-medium">
                            {payment.amount} {payment.currency}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Provider</p>
                          <p className="font-medium">{payment.provider}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Reference</p>
                          <p className="font-medium text-xs">{payment.providerReference}</p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-4">No payment information found</p>
                )}
              </div>
            )}
          </>
        )}

        <div className="flex justify-end pt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </StyledDialog>
  );
}